import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './pill-tabs.scss';

export interface PillTabItem {
    /** Value written back through onChange — must be unique. */
    key: string;
    label: string;
    /** Optional 16px icon (img/svg element). */
    icon?: React.ReactNode;
    /** Optional supporting line rendered under the label (rich variant). */
    description?: string;
}

export interface PillTabsProps {
    tabs: PillTabItem[];
    /** Key of the currently active tab — owned by the parent. */
    activeKey: string;
    onChange: (key: string) => void;
    /** Stretch the tabs to fill the available width instead of hugging their labels. */
    fullWidth?: boolean;
    /** Card-style tabs (icon tile, roomier padding). Defaults to on when any
     *  tab carries a description, but can be asked for on its own. */
    rich?: boolean;
    /** Extra class on the container (for page-level spacing only). */
    className?: string;
    ariaLabel?: string;
}

/**
 * Horizontal pill tabs with a single sliding gradient indicator.
 *
 * A gradient cannot be tweened by CSS, so an active-tab background would snap.
 * Instead one absolutely positioned gradient pill is measured against the active
 * button and moved with transform + width, which the compositor can animate.
 *
 * Fully self-contained and scoped to `.inv-pill-tabs` — it owns no tab state.
 */
export const PillTabs: React.FC<PillTabsProps> = ({
    tabs,
    activeKey,
    onChange,
    fullWidth = false,
    rich,
    className,
    ariaLabel,
}) => {
    const listRef = useRef<HTMLDivElement | null>(null);
    const [indicator, setIndicator] = useState({ left: 0, top: 0, width: 0, height: 0 });
    // Transitions stay off until after the first paint so the pill does not
    // slide in from the left on mount — it only animates on real tab switches.
    const [isAnimated, setIsAnimated] = useState(false);

    const measure = useCallback(() => {
        const list = listRef.current;
        if (!list) return;

        const activeBtn = list.querySelector<HTMLButtonElement>('[data-pill-active="true"]');
        if (!activeBtn) return;

        /* Both axes, so the indicator still finds its tab when the tabs are
           stacked in a column (narrow screens) instead of laid out in a row. */
        setIndicator({
            left: activeBtn.offsetLeft,
            top: activeBtn.offsetTop,
            width: activeBtn.offsetWidth,
            height: activeBtn.offsetHeight,
        });
    }, []);

    // Position before paint → no flash of a mis-placed indicator.
    useLayoutEffect(() => {
        measure();
    }, [measure, activeKey, tabs, fullWidth]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => setIsAnimated(true));
        return () => cancelAnimationFrame(frame);
    }, []);

    // Re-measure when the available width changes, and when a late-loading
    // webfont changes the label widths.
    useEffect(() => {
        window.addEventListener('resize', measure);

        let observer: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined' && listRef.current) {
            observer = new ResizeObserver(measure);
            observer.observe(listRef.current);
        }

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [measure]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(e.key)) return;

        const current = tabs.findIndex((tab) => tab.key === activeKey);
        if (current === -1) return;

        let next = current;
        if (e.key === 'ArrowRight') next = (current + 1) % tabs.length;
        if (e.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = tabs.length - 1;

        if (next === current) return;

        e.preventDefault();
        onChange(tabs[next].key);
    };

    return (
        <div
            ref={listRef}
            role="tablist"
            aria-label={ariaLabel}
            onKeyDown={handleKeyDown}
            className={[
                'inv-pill-tabs',
                fullWidth ? 'inv-pill-tabs--full' : '',
                rich ?? tabs.some((tab) => tab.description) ? 'inv-pill-tabs--rich' : '',
                isAnimated ? 'is-animated' : '',
                className || '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <span
                aria-hidden="true"
                className="inv-pill-tabs__indicator"
                style={{
                    transform: `translate(${indicator.left}px, ${indicator.top}px)`,
                    width: `${indicator.width}px`,
                    height: `${indicator.height}px`,
                }}
            />

            {tabs.map((tab) => {
                const isActive = tab.key === activeKey;

                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        tabIndex={isActive ? 0 : -1}
                        data-pill-active={isActive}
                        className={`inv-pill-tabs__tab${isActive ? ' is-active' : ''}`}
                        onClick={() => onChange(tab.key)}
                    >
                        {tab.icon && (
                            <span className="inv-pill-tabs__icon" aria-hidden="true">
                                {tab.icon}
                            </span>
                        )}
                        <span className="inv-pill-tabs__text">
                            <span className="inv-pill-tabs__label">{tab.label}</span>
                            {tab.description && (
                                <span className="inv-pill-tabs__desc">{tab.description}</span>
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export default PillTabs;
